/**
 * Client-side persistence for Next Round Eligibility investigation-results attachment.
 * Backend is source of truth; local state prevents re-hydrating explicitly removed docs.
 */

const STORAGE_PREFIX = 'fundelite_investigation_results_v1';

export function investigationResultsStorageKey(userId) {
  const id = String(userId || 'anonymous').trim();
  return `${STORAGE_PREFIX}:${id}`;
}

export function loadInvestigationResultsState(userId) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(investigationResultsStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function saveInvestigationResultsState(userId, state) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      investigationResultsStorageKey(userId),
      JSON.stringify({
        ...state,
        updatedAt: new Date().toISOString(),
      })
    );
  } catch {
    /* ignore quota errors */
  }
}

export function markInvestigationResultsDetached(userId, submissionId) {
  saveInvestigationResultsState(userId, {
    detached: true,
    submission: null,
    detachedSubmissionId: submissionId || null,
    detachedAt: new Date().toISOString(),
  });
}

export function markInvestigationResultsAttached(userId, submission) {
  saveInvestigationResultsState(userId, {
    detached: false,
    submission: submission || null,
    detachedSubmissionId: null,
    detachedAt: null,
  });
}

/**
 * Merge API submission with explicit local detach/replace hints.
 */
export function resolveInvestigationSubmission(apiSubmission, localState) {
  if (!localState) {
    return apiSubmission || null;
  }

  if (localState.detached) {
    if (!apiSubmission) return null;
    if (
      localState.detachedSubmissionId &&
      apiSubmission.submission_id === localState.detachedSubmissionId
    ) {
      return null;
    }
    if (localState.detachedAt && apiSubmission.uploaded_at) {
      const detachedMs = new Date(localState.detachedAt).getTime();
      const uploadedMs = new Date(apiSubmission.uploaded_at).getTime();
      if (!Number.isNaN(detachedMs) && !Number.isNaN(uploadedMs) && uploadedMs <= detachedMs) {
        return null;
      }
    }
    return apiSubmission;
  }

  return apiSubmission || localState.submission || null;
}

export function normalizeInvestigationSubmission(raw) {
  if (!raw) return null;
  const submissionId = raw.submission_id || raw.id;
  if (!submissionId) return null;
  return {
    submission_id: submissionId,
    bureau: raw.bureau || 'experian',
    filename: raw.filename || raw.title || 'Investigation results',
    title: raw.title || raw.filename || 'Investigation results',
    document_id: raw.document_id || null,
    source: raw.source || (raw.document_id ? 'existing_document' : 'upload'),
    investigation_completed: Boolean(raw.investigation_completed ?? true),
    uploaded_at: raw.uploaded_at || null,
    thumbnail_url: raw.thumbnail_url || raw.file_url || raw.cropped_url || null,
    file_url: raw.file_url || raw.cropped_url || raw.thumbnail_url || null,
    is_existing_document_reference: Boolean(
      raw.is_existing_document_reference || raw.document_id
    ),
  };
}
